import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  LineChart, Line, LabelList
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Download, Upload, TrendingUp, Users, IndianRupee, Award,
  FileText, CheckCircle, Clock, AlertCircle, Search, RefreshCw,
  BarChart2, ClipboardList, Star, BookOpen, Calendar, Pencil, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MonthlyAttendanceTab from './MonthlyAttendanceTab';
import {
  StaffAdvanceWorkIndexTab,
  DailyWorkReportIndexTab,
  DailyScheduleReportIndexTab,
  StaffReportIndexTab,
  DailyWorkTab,
  DailyScheduleTab,
  CompanyHierarchyTab,
  ProjectHierarchyTab,
} from '../../components/hr-content/HRReportsSubtabs';
import SalaryPayrollTab from '../../components/hr-content/SalaryPayrollTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

// Helper: update a single cell in the HR report via PATCH
const updateReportCell = async (section, name, field, value, srNo = null) => {
  try {
    await axios.patch(`${API}/hr/reports/update-cell`,
      { section, name, field, value, srNo },
      { headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` } }
    );
  } catch {
    toast.error('Failed to save change');
  }
};

// Status cycle orders
const PERF_CYCLE  = ['DONE', 'INP', 'PENDING', ''];
const SETUP_CYCLE = ['OK', 'INP', 'PENDING', ''];
const INDEX_CYCLE = ['DONE', 'INPROCESS', 'PENDING', ''];

const STATUS_BADGE = {
  'DONE': 'bg-green-100 text-green-700 border border-green-200',
  'OK': 'bg-green-100 text-green-700 border border-green-200',
  'INPROCESS': 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  'INFROCESS': 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  'INP': 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  'INP.': 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  'PENDING': 'bg-red-100 text-red-700 border border-red-200',
  'NILL': 'bg-stone-100 text-stone-500 border border-stone-200',
  'IN WORKING': 'bg-blue-100 text-blue-700 border border-blue-200',
};

function StatusBadge({ val }) {
  const v = (val || '').toUpperCase().trim();
  const cls = STATUS_BADGE[v] || 'bg-stone-100 text-stone-500 border border-stone-200';
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{val || '-'}</span>;
}

// Inline editable components for report edit mode
function EditableStatus({ val, onSave, cycle = PERF_CYCLE }) {
  const norm = (val || '').toUpperCase().trim();
  return (
    <button
      onClick={() => { const i = cycle.findIndex(s => s === norm); onSave(cycle[(i + 1) % cycle.length] || null); }}
      title="Click to change"
      className="cursor-pointer hover:opacity-75 transition-opacity"
    >
      <StatusBadge val={val} />
    </button>
  );
}

function EditableNumber({ val, onSave, min = 0, max = 99999999 }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(String(val ?? ''));
  if (!editing) return (
    <span className="cursor-pointer underline decoration-dotted hover:text-stone-900" onClick={() => { setV(String(val ?? '')); setEditing(true); }}>{val ?? '-'}</span>
  );
  return (
    <input type="number" min={min} max={max} value={v} autoFocus
      className="w-20 text-center border border-blue-400 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
      onChange={e => setV(e.target.value)}
      onBlur={() => { setEditing(false); const n = Number(v); if (!isNaN(n)) onSave(n); }}
      onKeyDown={e => {
        if (e.key === 'Enter') { setEditing(false); const n = Number(v); if (!isNaN(n)) onSave(n); }
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
}

function EditableText({ val, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(val || '');
  if (!editing) return (
    <span className="cursor-pointer hover:underline decoration-dotted text-stone-600" onClick={() => { setV(val || ''); setEditing(true); }}>{val || '-'}</span>
  );
  return (
    <input type="text" value={v} autoFocus
      className="w-full min-w-[100px] border border-blue-400 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
      onChange={e => setV(e.target.value)}
      onBlur={() => { setEditing(false); if (v !== (val || '')) onSave(v); }}
      onKeyDown={e => {
        if (e.key === 'Enter') { setEditing(false); if (v !== (val || '')) onSave(v); }
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
}

// Edit toggle button used in each section's card header
function EditBtn({ editMode, onToggle }) {
  return (
    <button
      onClick={onToggle}
      data-testid="report-edit-toggle"
      className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold transition-all ${
        editMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-stone-800 text-white hover:bg-stone-700'
      }`}
    >
      {editMode ? <><Check className="w-3 h-3" />Done</> : <><Pencil className="w-3 h-3" />Edit</>}
    </button>
  );
}

function SectionButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
        active
          ? 'bg-stone-800 text-white shadow'
          : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
      }`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </button>
  );
}

function KPICard({ icon: Icon, label, value, sub, color = 'blue' }) {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    amber: 'from-amber-500 to-amber-600',
    violet: 'from-violet-500 to-violet-600',
    teal: 'from-teal-500 to-teal-600',
    rose: 'from-rose-500 to-rose-600',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-4 text-white shadow-sm`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 opacity-80" />
        <span className="text-xs opacity-70">{sub}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs opacity-80 mt-1">{label}</div>
    </div>
  );
}

// ---- Sub-section components ----

function OverviewSection({ overview }) {
  if (!overview) return <div className="text-stone-400 py-8 text-center">Loading overview...</div>;
  const deptData = overview.deptDistribution || [];
  const statusData = [
    { name: 'Done', value: overview.indexDone || 0, color: '#22c55e' },
    { name: 'In Process', value: overview.indexInProcess || 0, color: '#f59e0b' },
    { name: 'Pending', value: overview.indexPending || 0, color: '#ef4444' },
  ];
  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard icon={Users} label="Employees (CTC)" value={overview.totalEmployeesCTC || 0} color="blue" />
        <KPICard icon={IndianRupee} label="Monthly Salary" value={`₹${((overview.totalMonthlySalary || 0) / 1000).toFixed(0)}K`} sub="FY 2025-26" color="green" />
        <KPICard icon={IndianRupee} label="Yearly Salary" value={`₹${((overview.totalYearlySalary || 0) / 100000).toFixed(1)}L`} sub="FY 2025-26" color="teal" />
        <KPICard icon={TrendingUp} label="Performance" value={`${overview.performanceCompletePct || 0}%`} sub="Completion" color="violet" />
        <KPICard icon={Star} label="Avg Score" value={`${overview.avgPerformanceScore || 0}/10`} sub="Performance" color="amber" />
        <KPICard icon={FileText} label="Doc Compliance" value={`${overview.documentCompliancePct || 0}%`} sub="Staff Setup" color="rose" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* HR Index Status Pie */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-stone-500" />
              HR Checklist Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie isAnimationActive={false} data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {statusData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Dept Distribution Bar */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-stone-500" />
              Department Distribution (Weekly Leave)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={deptData} margin={{ left: -10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar isAnimationActive={false} dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Progress bars */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Skill Completion', pct: overview.skillCompletePct || 0, color: 'bg-violet-500' },
          { label: 'Performance Completion', pct: overview.performanceCompletePct || 0, color: 'bg-green-500' },
          { label: 'Document Compliance', pct: overview.documentCompliancePct || 0, color: 'bg-amber-500' },
        ].map(({ label, pct, color }) => (
          <Card key={label}>
            <CardContent className="pt-4 pb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-stone-700">{label}</span>
                <span className="font-bold text-stone-800">{pct}%</span>
              </div>
              <div className="w-full bg-stone-100 rounded-full h-3">
                <div className={`${color} h-3 rounded-full transition-all`} style={{ width: `${pct}%` }} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function CTCSection({ ctc, totals, joiningDates = {} }) {
  const [search, setSearch] = useState('');
  const [editMode, setEditMode] = useState(false);
  const YEARS = ['2223', '2324', '2425', '2526'];
  const REMARK_TO_NUM = { 'DONE': 3, 'IN WORKING': 2, 'CTC PENDING': 1 };

  // FY April-March: yearCode '2223' => Apr 2022 → Mar 2023.
  // Returns whole months (0–12) employee was present in that FY based on joining date.
  const computeMonthsInFY = (joiningDateStr, yearCode) => {
    if (!joiningDateStr) return 12; // unknown joining date → assume full year
    const join = new Date(joiningDateStr);
    if (isNaN(join.getTime())) return 12;
    const fyStartYear = 2000 + parseInt(yearCode.slice(0, 2), 10); // '2526' → 2025
    const fyEndYear = 2000 + parseInt(yearCode.slice(2, 4), 10);   // '2526' → 2026
    const fyStart = new Date(fyStartYear, 3, 1);  // Apr 1
    const fyEnd = new Date(fyEndYear, 2, 31);     // Mar 31
    if (join > fyEnd) return 0;          // not yet joined this FY
    if (join <= fyStart) return 12;      // full FY
    // Joined mid-FY: count from join month to Mar (inclusive)
    const months = (fyEnd.getFullYear() - join.getFullYear()) * 12 + (fyEnd.getMonth() - join.getMonth()) + 1;
    return Math.max(0, Math.min(12, months));
  };

  // Hydrate: ensure every row has auto-computed totalPresentMonth* (from joining date) — read-only
  const hydrate = (rows) => (rows || []).map(r => {
    const u = { ...r };
    const joining = joiningDates[(u.name || '').toUpperCase().trim()];
    YEARS.forEach(y => {
      u[`monthsInFY${y}`] = computeMonthsInFY(joining, y); // raw month count (0–12)
      // amount earned in that FY = monthlySalary × monthsInFY
      const monthly = Number(u[`monthlySalary${y}`]) || 0;
      u[`totalPresentMonth${y}`] = monthly * u[`monthsInFY${y}`];
    });
    return u;
  });

  const [localCtc, setLocalCtc] = useState(hydrate(ctc));
  useEffect(() => { setLocalCtc(hydrate(ctc)); /* eslint-disable-next-line */ }, [ctc, joiningDates]);

  const filtered = localCtc.filter(r => (r.name || '').toLowerCase().includes(search.toLowerCase()));

  // Helper: full edit with cascading auto-calcs
  // Formula: Yearly CTC AMT [year] = (% of CTC) × (Monthly Salary [year]) × (Total Present Month [year])

  const recomputeYearlyCTC = (row, year) => {
    const pct = Number(row.pctCTC) || 0;
    const monthly = Number(row[`monthlySalary${year}`]) || 0;
    const months = Number(row[`monthsInFY${year}`]) || 0;
    return pct * monthly * months;
  };

  const handleEdit = (name, field, value) => {
    setLocalCtc(prev => prev.map(r => {
      if (r.name !== name) return r;
      const u = { ...r, [field]: value };

      // 1. Edit Monthly Salary[year] → recompute Yearly Salary[year] AND Total Present Month amount [year]
      if (field.startsWith('monthlySalary')) {
        const y = field.replace('monthlySalary', '');
        const yearly = (Number(value) || 0) * 12;
        u[`yearlySalary${y}`] = yearly;
        updateReportCell('ctc', name, `yearlySalary${y}`, yearly);
        // Total Present Month amount = monthlySalary × monthsInFY
        const months = Number(u[`monthsInFY${y}`]) || 0;
        const presentAmt = (Number(value) || 0) * months;
        u[`totalPresentMonth${y}`] = presentAmt;
        updateReportCell('ctc', name, `totalPresentMonth${y}`, presentAmt);
      }

      // 2. Decide which years' Yearly CTC AMT to auto-recompute
      let yearsToRecompute = [];
      if (field === 'pctCTC') {
        yearsToRecompute = [...YEARS]; // all years
      } else if (field.startsWith('monthlySalary')) {
        yearsToRecompute = [field.replace('monthlySalary', '')];
      }
      yearsToRecompute.forEach(y => {
        const v = recomputeYearlyCTC(u, y);
        u[`yearlyCTC${y}`] = v;
        updateReportCell('ctc', name, `yearlyCTC${y}`, v);
      });

      // 3. Recompute Total CTC AMT = sum of Yearly CTC AMT across all 4 years
      if (yearsToRecompute.length > 0 || field.startsWith('yearlyCTC')) {
        const total = YEARS.reduce((s, y) => s + (Number(u[`yearlyCTC${y}`]) || 0), 0);
        u.totalCTC = total;
        updateReportCell('ctc', name, 'totalCTC', total);
      }

      // 4. Recompute Target Bal whenever Total CTC or Target Complete changes
      if (yearsToRecompute.length > 0 || field.startsWith('yearlyCTC') || field === 'targetComplete' || field === 'totalCTC') {
        const bal = (Number(u.totalCTC) || 0) - (Number(u.targetComplete) || 0);
        u.targetBal = bal;
        updateReportCell('ctc', name, 'targetBal', bal);
      }

      // 5. Edit Remark → auto-set Number
      if (field === 'remark') {
        const n = REMARK_TO_NUM[String(value).toUpperCase().trim()] || 1;
        u.number = n;
        updateReportCell('ctc', name, 'number', n);
      }

      return u;
    }));
    updateReportCell('ctc', name, field, value);
  };

  // Chart 1: Monthly Salary for ALL employees
  const chart1Data = localCtc.map(r => ({
    name: r.name?.split(' ')[0] || r.name,
    salary: r.monthlySalary2526 || 0,
  }));

  // Chart 2: Total CTC / Target Done / Target Bal for ALL employees
  const chart2Data = localCtc.map(r => ({
    name: r.name || '',
    totalCTC: r.totalCTC || 0,
    targetDone: r.targetComplete || 0,
    targetBal: r.targetBal != null ? Math.abs(r.targetBal) : 0,
    targetBalNeg: r.targetBal != null && r.targetBal < 0,
  }));

  // Compute chart width: 40px per bar for chart1, 100px per group for chart2 (room for full names)
  const chart1Width = Math.max(chart1Data.length * 40 + 80, 800);
  const chart2Width = Math.max(chart2Data.length * 100 + 80, 800);

  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '-';

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-4"><div className="text-xs text-stone-500 mb-1">Total Monthly (2025-26)</div><div className="text-xl font-bold text-green-600">{fmt(totals?.totalMonthly2526)}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-xs text-stone-500 mb-1">Total Yearly (2025-26)</div><div className="text-xl font-bold text-blue-600">{fmt(totals?.totalYearly2526)}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-xs text-stone-500 mb-1">Total CTC (All Years)</div><div className="text-xl font-bold text-violet-600">{fmt(totals?.totalAllCTC)}</div></CardContent></Card>
      </div>

      {/* Chart wrapper — flex-col-reverse so the dark COST TO COMPANY chart appears above the light Monthly Salary chart */}
      <div className="flex flex-col-reverse gap-5">
      {/* Chart 1 - Monthly Salary (all employees, scrollable) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Monthly Salary Distribution (FY 2025-26) — {chart1Data.length} Employees</CardTitle>
        </CardHeader>
        <CardContent className="pr-2">
          <div className="overflow-x-auto pb-3" style={{ cursor: 'grab' }}>
            <div style={{ width: chart1Width, minWidth: chart1Width }}>
              <BarChart
                width={chart1Width}
                height={240}
                data={chart1Data}
                margin={{ top: 5, right: 20, left: 10, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-40} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={v => [`₹${Number(v).toLocaleString('en-IN')}`, 'Monthly Salary']} />
                <Bar isAnimationActive={false} dataKey="salary" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chart 2 - COST TO COMPANY (Excel-style dark theme) */}
      <Card className="bg-stone-800 border-stone-700 text-stone-100 overflow-hidden">
        <CardHeader className="pb-2 bg-stone-800">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 text-center">
              <h3 className="inline-block text-base font-extrabold tracking-wider text-white underline underline-offset-4 decoration-white/70">
                COST TO COMPANY
              </h3>
              <p className="text-[11px] text-stone-300 mt-1">
                Total CTC vs Target Done vs Target Balance — {chart2Data.length} Employees
              </p>
            </div>
            <div className="flex flex-col gap-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-[11px] font-semibold">
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#ef4444]" /> TOTAL CTC AMT</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#22c55e]" /> TARGET COMPLETE</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#facc15]" /> TARGET BALANCE</div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pr-2 bg-gradient-to-b from-stone-800 to-stone-900">
          <div className="overflow-x-auto pb-3">
            <div style={{ width: chart2Width, minWidth: chart2Width }}>
              <BarChart
                width={chart2Width}
                height={420}
                data={chart2Data}
                margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                barCategoryGap="20%"
                barGap={2}
              >
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.12)" vertical={false} />
                <XAxis
                  dataKey="name"
                  interval={0}
                  axisLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                  tickLine={false}
                  height={1}
                  tick={false}
                />
                <YAxis
                  width={60}
                  tick={{ fontSize: 10, fill: '#e7e5e4' }}
                  domain={[0, 40000000]}
                  ticks={[
                    0, 2500000, 5000000, 7500000, 10000000,
                    12500000, 15000000, 17500000, 20000000,
                    22500000, 25000000, 27500000, 30000000,
                    32500000, 35000000, 37500000, 40000000,
                  ]}
                  tickFormatter={(v) => {
                    if (v === 0) return '₹0L';
                    if (v < 10000000) return `₹${(v / 100000).toFixed(0)}L`;
                    const cr = v / 10000000;
                    return `₹${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(2).replace(/\.?0+$/, '')}Cr`;
                  }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                  tickLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                />
                <Tooltip
                  formatter={(v, n) => [`₹${Number(v).toLocaleString('en-IN')}`, n]}
                  contentStyle={{
                    background: '#1c1917',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 6,
                    color: '#fafaf9',
                    fontSize: 11,
                  }}
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                />
                <Bar isAnimationActive={false} dataKey="totalCTC" fill="#ef4444" name="TOTAL CTC AMT" />
                <Bar isAnimationActive={false} dataKey="targetDone" fill="#22c55e" name="TARGET COMPLETE" />
                <Bar isAnimationActive={false} dataKey="targetBal" fill="#facc15" name="TARGET BALANCE" />
              </BarChart>

              {/* Aligned data table — columns line up under each bar group */}
              <div
                className="border-t-2 border-white/40"
                style={{
                  display: 'grid',
                  gridTemplateColumns: `60px repeat(${chart2Data.length}, 1fr) 20px`,
                }}
              >
                {/* Row 0: Employee names (Excel-style upright multi-line) */}
                <div className="sticky left-0 z-10 bg-stone-900 text-stone-300 text-[9px] font-bold px-1 py-1.5 flex items-center justify-center border border-white/20 leading-tight text-center">
                  EMPLOYEE
                </div>
                {chart2Data.map((d, i) => (
                  <div
                    key={`nm-${i}`}
                    className="text-[9px] font-bold text-stone-100 text-center px-0.5 py-1.5 border-r border-b border-white/15 bg-stone-900 leading-[1.15] break-words"
                    style={{ minHeight: 60 }}
                  >
                    {String(d.name || '').toUpperCase().split(/\s+/).map((w, wi) => (
                      <div key={wi}>{w}</div>
                    ))}
                  </div>
                ))}
                <div className="border-b border-white/15 bg-stone-900" />

                {/* Row 1: TOTAL CTC AMT */}
                <div className="sticky left-0 z-10 bg-[#ef4444] text-white text-[10px] font-bold px-2 py-1.5 flex items-center justify-center border border-white/20 leading-tight text-center">
                  TOTAL CTC AMT
                </div>
                {chart2Data.map((d, i) => (
                  <div key={`ctc-${i}`} className="text-[10px] text-stone-100 text-center py-1.5 border-r border-b border-white/10 bg-stone-900/60 truncate">
                    {d.totalCTC ? Number(d.totalCTC).toLocaleString('en-IN') : '0'}
                  </div>
                ))}
                <div className="border-b border-white/10" />

                {/* Row 2: TARGET COMPLETE */}
                <div className="sticky left-0 z-10 bg-[#22c55e] text-white text-[10px] font-bold px-2 py-1.5 flex items-center justify-center border border-white/20 leading-tight text-center">
                  TARGET COMPLETE
                </div>
                {chart2Data.map((d, i) => (
                  <div key={`tc-${i}`} className="text-[10px] text-stone-100 text-center py-1.5 border-r border-b border-white/10 bg-stone-900/40 truncate">
                    {d.targetDone ? Number(d.targetDone).toLocaleString('en-IN') : '0'}
                  </div>
                ))}
                <div className="border-b border-white/10" />

                {/* Row 3: TARGET BALANCE */}
                <div className="sticky left-0 z-10 bg-[#facc15] text-stone-900 text-[10px] font-bold px-2 py-1.5 flex items-center justify-center border border-white/20 leading-tight text-center">
                  TARGET BALANCE
                </div>
                {chart2Data.map((d, i) => (
                  <div key={`tb-${i}`} className="text-[10px] text-stone-100 text-center py-1.5 border-r border-white/10 bg-stone-900/60 truncate">
                    {d.targetBal ? `${d.targetBalNeg ? '-' : ''}${Number(d.targetBal).toLocaleString('en-IN')}` : '0'}
                  </div>
                ))}
                <div />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table — Excel-style COST TO COMPANY 2022-23-24-25-26 */}
      </div>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center gap-3 flex-wrap">
            <CardTitle className="text-sm font-semibold">COST TO COMPANY 2022-23-24-25-26 — {filtered.length} Employees</CardTitle>
            <div className="flex items-center gap-2">
              <EditBtn editMode={editMode} onToggle={() => setEditMode(m => !m)} />
              <div className="relative w-48">
                <Search className="absolute left-2 top-2 w-4 h-4 text-stone-400" />
                <Input className="pl-8 h-8 text-xs" placeholder="Search name..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto" style={{ maxHeight: 600 }}>
            <table className="text-xs border-collapse" style={{ minWidth: 1700 }}>
              {/* Two-row header to match Excel grouping */}
              <thead className="sticky top-0 z-20">
                <tr className="bg-yellow-300 text-stone-900">
                  <th colSpan={20} className="px-3 py-2 text-center text-sm font-extrabold tracking-wide border border-stone-700">
                    COST TO COMPANY 2022-23-24-25-26
                  </th>
                </tr>
                <tr className="bg-stone-100 text-stone-700">
                  <th rowSpan={2} className="px-2 py-1.5 text-left font-semibold border border-stone-300 sticky left-0 bg-stone-100 z-10 w-12">SR. NO</th>
                  <th rowSpan={2} className="px-2 py-1.5 text-left font-semibold border border-stone-300 sticky bg-stone-100 z-10 min-w-[150px]" style={{ left: 48 }}>NAME</th>
                  <th rowSpan={2} className="px-2 py-1.5 text-center font-semibold border border-stone-300 w-14">% OF<br/>CTC</th>
                  <th colSpan={4} className="px-2 py-1.5 text-center font-semibold border border-stone-300">MONTHLY SALARY</th>
                  <th colSpan={4} className="px-2 py-1.5 text-center font-semibold border border-stone-300">YEARLY SALARY</th>
                  <th colSpan={4} className="px-2 py-1.5 text-center font-semibold border border-stone-300">YEARLY CTC AMT</th>
                  <th rowSpan={2} className="px-2 py-1.5 text-center font-semibold border border-stone-300 min-w-[100px]">TOTAL CTC<br/>AMT</th>
                  <th rowSpan={2} className="px-2 py-1.5 text-center font-semibold border border-stone-300 min-w-[100px]">TARGET<br/>COMPLETE</th>
                  <th rowSpan={2} className="px-2 py-1.5 text-center font-semibold border border-stone-300 min-w-[100px]">TARGET<br/>BAL</th>
                  <th rowSpan={2} className="px-2 py-1.5 text-center font-semibold border border-stone-300 w-12">NU<br/>MB<br/>ER</th>
                  <th rowSpan={2} className="px-2 py-1.5 text-center font-semibold border border-stone-300 min-w-[110px]">REMARK</th>
                </tr>
                <tr className="bg-stone-100 text-stone-700">
                  {YEARS.map(y => <th key={`m${y}`} className="px-2 py-1.5 text-center font-semibold border border-stone-300 w-20">{`20${y.slice(0,2)}-20${y.slice(2)}`}</th>)}
                  {YEARS.map(y => <th key={`pm${y}`} className="px-2 py-1.5 text-center font-semibold border border-stone-300 w-16">{`20${y.slice(0,2)}-20${y.slice(2)}`}</th>)}
                  {YEARS.map(y => <th key={`c${y}`} className="px-2 py-1.5 text-center font-semibold border border-stone-300 w-24">{`20${y.slice(0,2)}-20${y.slice(2)}`}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  // Determine row remark + color: red=CTC PENDING (1), yellow=IN WORKING (2), green=DONE (3)
                  const remarkRaw = (r.remark || (r.number === 3 ? 'DONE' : r.number === 2 ? 'IN WORKING' : 'CTC PENDING'));
                  const remark = String(remarkRaw).toUpperCase().trim();
                  const num = r.number || REMARK_TO_NUM[remark] || 1;
                  const rowBg = i % 2 === 0 ? 'bg-white' : 'bg-stone-50';
                  const rowText = 'text-stone-800';
                  const remarkBadge = remark === 'DONE'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : remark === 'IN WORKING'
                      ? 'bg-amber-100 text-amber-700 border border-amber-300'
                      : 'bg-red-100 text-red-700 border border-red-300';
                  const cellCls = `px-1.5 py-1 text-center border border-stone-200 ${rowText} font-medium`;
                  const editableCellCls = `px-1.5 py-1 text-center border border-stone-200 ${rowText}`;
                  return (
                    <tr key={i} className={`${rowBg} hover:bg-blue-50/40 transition-colors`}>
                      <td className={`${cellCls} sticky left-0 ${rowBg} z-10 font-semibold`}>{r.srNo || i + 1}</td>
                      <td className={`px-2 py-1 text-left border border-stone-200 sticky ${rowBg} z-10 font-bold ${rowText}`} style={{ left: 48 }}>{r.name}</td>
                      <td className={editableCellCls}>
                        {editMode ? <EditableNumber val={r.pctCTC} onSave={v => handleEdit(r.name, 'pctCTC', v)} /> : (r.pctCTC ?? '-')}
                      </td>
                      {/* Monthly Salary */}
                      {YEARS.map(y => (
                        <td key={`m${y}`} className={editableCellCls}>
                          {editMode
                            ? <EditableNumber val={r[`monthlySalary${y}`]} onSave={v => handleEdit(r.name, `monthlySalary${y}`, v)} />
                            : (r[`monthlySalary${y}`] != null ? Number(r[`monthlySalary${y}`]).toLocaleString('en-IN') : '-')}
                        </td>
                      ))}
                      {/* Yearly Salary (renamed — auto = Monthly × months present in FY) */}
                      {YEARS.map(y => {
                        const amt = Number(r[`totalPresentMonth${y}`]) || 0;
                        const months = Number(r[`monthsInFY${y}`]) || 0;
                        return (
                          <td key={`pm${y}`} className={`${cellCls} text-stone-800`} title={`Auto = Monthly Salary × ${months} month${months === 1 ? '' : 's'} present in FY${y.slice(0,2)}-${y.slice(2)}`}>
                            {months === 0 ? '-' : amt > 0 ? Number(amt).toLocaleString('en-IN') : '-'}
                          </td>
                        );
                      })}
                      {/* Yearly CTC Amt (auto = % CTC × Monthly Salary × Total Present Month) */}
                      {YEARS.map(y => (
                        <td key={`c${y}`} className={`${cellCls} text-stone-700`} title="Auto = % of CTC × Monthly Salary × Total Present Month">
                          {r[`yearlyCTC${y}`] != null ? Number(r[`yearlyCTC${y}`]).toLocaleString('en-IN') : '-'}
                        </td>
                      ))}
                      {/* Total CTC AMT (auto-derived = sum of Yearly CTC) */}
                      <td className={`${cellCls} font-bold text-blue-700`} title="Auto = Sum of Yearly CTC AMT">
                        {r.totalCTC != null ? Number(r.totalCTC).toLocaleString('en-IN') : '-'}
                      </td>
                      {/* Target Complete */}
                      <td className={editableCellCls + ' font-semibold text-emerald-700'}>
                        {editMode
                          ? <EditableNumber val={r.targetComplete} onSave={v => handleEdit(r.name, 'targetComplete', v)} />
                          : (r.targetComplete != null ? Number(r.targetComplete).toLocaleString('en-IN') : '-')}
                      </td>
                      {/* Target Bal (auto = Total CTC − Target Complete) */}
                      <td className={`${cellCls} font-semibold ${(r.targetBal || 0) < 0 ? 'text-red-600' : 'text-stone-700'}`} title="Auto = Total CTC − Target Complete">
                        {r.targetBal != null ? Number(r.targetBal).toLocaleString('en-IN') : '-'}
                      </td>
                      {/* Number (auto from Remark) */}
                      <td className={`${cellCls} font-bold`} title="Auto from Remark">{num}</td>
                      {/* Remark */}
                      <td className={`px-1.5 py-1 text-center border border-stone-200`}>
                        {editMode ? (
                          <select
                            className="bg-white border border-stone-300 rounded px-1 py-0.5 text-[11px] font-bold focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={remark}
                            onChange={(e) => handleEdit(r.name, 'remark', e.target.value)}
                          >
                            <option value="CTC PENDING">CTC PENDING</option>
                            <option value="IN WORKING">IN WORKING</option>
                            <option value="DONE">DONE</option>
                          </select>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${remarkBadge}`}>
                            {remark}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-stone-100 border-t-2">
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-right text-xs font-extrabold text-stone-800 border border-stone-400">TOTAL</td>
                  {YEARS.map(y => {
                    const total = filtered.reduce((s, r) => s + (Number(r[`monthlySalary${y}`]) || 0), 0);
                    return <td key={`tm${y}`} className="px-1.5 py-1.5 text-center text-xs font-bold border border-stone-400">{total ? total.toLocaleString('en-IN') : '-'}</td>;
                  })}
                  {YEARS.map(y => {
                    const total = filtered.reduce((s, r) => s + (Number(r[`totalPresentMonth${y}`]) || 0), 0);
                    return <td key={`tpm${y}`} className="px-1.5 py-1.5 text-center text-xs font-bold border border-stone-400">{total ? total.toLocaleString('en-IN') : '-'}</td>;
                  })}
                  {YEARS.map(y => {
                    const total = filtered.reduce((s, r) => s + (Number(r[`yearlyCTC${y}`]) || 0), 0);
                    return <td key={`tc${y}`} className="px-1.5 py-1.5 text-center text-xs font-bold border border-stone-400">{total ? total.toLocaleString('en-IN') : '-'}</td>;
                  })}
                  <td className="px-1.5 py-1.5 text-center text-xs font-extrabold border border-stone-400 text-blue-700">
                    {(() => { const t = filtered.reduce((s, r) => s + (Number(r.totalCTC) || 0), 0); return t ? t.toLocaleString('en-IN') : '-'; })()}
                  </td>
                  <td className="px-1.5 py-1.5 text-center text-xs font-extrabold border border-stone-400 text-emerald-700">
                    {(() => { const t = filtered.reduce((s, r) => s + (Number(r.targetComplete) || 0), 0); return t ? t.toLocaleString('en-IN') : '-'; })()}
                  </td>
                  <td className="px-1.5 py-1.5 text-center text-xs font-extrabold border border-stone-400 text-rose-700">
                    {(() => { const t = filtered.reduce((s, r) => s + (Number(r.targetBal) || 0), 0); return t ? t.toLocaleString('en-IN') : '-'; })()}
                  </td>
                  <td colSpan={2} className="border border-stone-400" />
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// Reusable Dark Grouped Bar Chart (DONE / INPROCESS / PENDING)
// Matches the Excel-style dark background report screenshots
// ============================================================
function DarkGroupedBarChart({ data, title, height = 300 }) {
  const PER_EMP_PX = 56; // narrow column → forces full name to wrap to 2-3 lines like Excel
  const Y_AXIS_PX = 50;  // width reserved for Y-axis tick labels — must match EMP column width
  const R_MARGIN = 20;
  const chartWidth = Math.max(data.length * PER_EMP_PX + Y_AXIS_PX + R_MARGIN, 800);

  return (
    <div style={{ backgroundColor: '#1a1a1a', borderRadius: '10px', padding: '18px 12px 8px' }}>
      {/* Header: Title + Legend (top-right like Excel) */}
      <div style={{ position: 'relative', minHeight: 28, marginBottom: 8 }}>
        <div style={{ textAlign: 'center', color: '#fff', fontWeight: '900', fontSize: '14px', letterSpacing: '1px', textDecoration: 'underline' }}>
          {title}
        </div>
        <div style={{
          position: 'absolute', top: 0, right: 4,
          background: '#fff', color: '#000',
          padding: '4px 8px', fontSize: '10px', fontWeight: 700,
          border: '1px solid #444', display: 'flex', flexDirection: 'column', gap: 2
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#00B050' }} /> DONE</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#FFD700' }} /> INPROCESS</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#FF0000' }} /> PENDING</div>
        </div>
      </div>

      {/* Single scroll container — chart + 4-row name+values table share one horizontal scrollbar */}
      <div className="overflow-x-auto pb-2" style={{ scrollbarColor: '#444 #1a1a1a' }}>
        <div style={{ width: chartWidth, minWidth: chartWidth }}>
          <BarChart
            width={chartWidth}
            height={height}
            data={data}
            margin={{ top: 10, right: R_MARGIN, left: 0, bottom: 0 }}
            barCategoryGap="20%"
            barGap={2}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
            <XAxis dataKey="name" interval={0} tick={false} axisLine={{ stroke: 'rgba(255,255,255,0.4)' }} height={1} />
            <YAxis
              width={Y_AXIS_PX}
              tick={{ fill: '#ccc', fontSize: 10 }}
              ticks={[0, 6, 12, 18, 24]}
              domain={[0, 24]}
              axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
              tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#2a2a2a', border: '1px solid #555', borderRadius: '6px', color: '#fff' }}
              itemStyle={{ color: '#fff', fontSize: '12px' }}
              labelStyle={{ color: '#fff', fontWeight: 'bold' }}
            />
            <Bar isAnimationActive={false} dataKey="done" fill="#00B050" name="DONE" maxBarSize={14} />
            <Bar isAnimationActive={false} dataKey="inprocess" fill="#FFD700" name="INPROCESS" maxBarSize={14} />
            <Bar isAnimationActive={false} dataKey="pending" fill="#FF0000" name="PENDING" maxBarSize={14} />
          </BarChart>

          {/* Integrated 4-row data table — column 1 = Y_AXIS_PX so EMP/DONE/INPROCESS/PENDING sits under the Y-axis labels; data columns line up under each bar group */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `${Y_AXIS_PX}px repeat(${data.length}, 1fr) ${R_MARGIN}px`,
              border: '1px solid rgba(255,255,255,0.4)',
              marginTop: 0,
            }}
          >
            {/* Row 0: Employee names — horizontal text, wraps within narrow cell (matches Excel layout) */}
            <div style={{
              position: 'sticky', left: 0, zIndex: 5,
              background: '#000', color: '#fff', fontSize: 10, fontWeight: 800,
              padding: '6px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRight: '1px solid rgba(255,255,255,0.3)',
              borderBottom: '1px solid rgba(255,255,255,0.3)',
            }}>EMP</div>
            {data.map((d, i) => (
              <div
                key={`nm-${i}`}
                style={{
                  background: '#000', color: '#fff',
                  fontSize: 10, fontWeight: 700, lineHeight: 1.2,
                  padding: '6px 3px', textAlign: 'center',
                  borderRight: '1px solid rgba(255,255,255,0.15)',
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                  minHeight: 60,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  hyphens: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {(d.fullName || d.name || '').toUpperCase()}
              </div>
            ))}
            <div style={{ background: '#000', borderBottom: '1px solid rgba(255,255,255,0.3)' }} />

            {/* Row 1: DONE */}
            <div style={{
              position: 'sticky', left: 0, zIndex: 5,
              background: '#00B050', color: '#fff', fontSize: 11, fontWeight: 800,
              padding: '6px 4px', textAlign: 'center',
              borderRight: '1px solid rgba(255,255,255,0.3)',
              borderBottom: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>■ DONE</div>
            {data.map((d, i) => (
              <div key={`d-${i}`} style={{
                background: '#111', color: '#00FF6A',
                fontSize: 12, fontWeight: 800, padding: '6px 2px', textAlign: 'center',
                borderRight: '1px solid rgba(255,255,255,0.06)',
                borderBottom: '1px solid rgba(255,255,255,0.2)',
              }}>{d.done ?? 0}</div>
            ))}
            <div style={{ background: '#111', borderBottom: '1px solid rgba(255,255,255,0.2)' }} />

            {/* Row 2: INPROCESS */}
            <div style={{
              position: 'sticky', left: 0, zIndex: 5,
              background: '#FFD700', color: '#000', fontSize: 11, fontWeight: 800,
              padding: '6px 4px', textAlign: 'center',
              borderRight: '1px solid rgba(255,255,255,0.3)',
              borderBottom: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>■ INPROCESS</div>
            {data.map((d, i) => (
              <div key={`p-${i}`} style={{
                background: '#181818', color: '#FFD700',
                fontSize: 12, fontWeight: 800, padding: '6px 2px', textAlign: 'center',
                borderRight: '1px solid rgba(255,255,255,0.06)',
                borderBottom: '1px solid rgba(255,255,255,0.2)',
              }}>{d.inprocess ?? 0}</div>
            ))}
            <div style={{ background: '#181818', borderBottom: '1px solid rgba(255,255,255,0.2)' }} />

            {/* Row 3: PENDING */}
            <div style={{
              position: 'sticky', left: 0, zIndex: 5,
              background: '#FF0000', color: '#fff', fontSize: 11, fontWeight: 800,
              padding: '6px 4px', textAlign: 'center',
              borderRight: '1px solid rgba(255,255,255,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>■ PENDING</div>
            {data.map((d, i) => (
              <div key={`x-${i}`} style={{
                background: '#111', color: '#FF6B6B',
                fontSize: 12, fontWeight: 800, padding: '6px 2px', textAlign: 'center',
                borderRight: '1px solid rgba(255,255,255,0.06)',
              }}>{d.pending ?? 0}</div>
            ))}
            <div style={{ background: '#111' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Dark Table-only variant — used for ALL EMPLOYEE PERFORMANCE REPORT
// (matches user screenshot: dark bg, employee names as columns,
// 3 rows for DONE / INPROCESS / PENDING with color-coded counts)
// ============================================================
function DarkPerformanceTable({ data, title }) {
  const PER_EMP_PX = 50;
  const NAME_COL_PX = 130;
  const tableWidth = Math.max(NAME_COL_PX + data.length * PER_EMP_PX, 800);

  return (
    <div style={{ backgroundColor: '#1a1a1a', borderRadius: '10px', padding: '18px 12px 14px' }}>
      {/* Title */}
      <div style={{ textAlign: 'center', color: '#fff', fontWeight: '900', fontSize: '14px', letterSpacing: '1px', textDecoration: 'underline', marginBottom: '14px' }}>
        {title}
      </div>

      {/* Scrollable Dark Table */}
      <div className="overflow-x-auto pb-2" style={{ scrollbarColor: '#444 #1a1a1a' }} data-testid="dark-perf-table-scroll">
        <div style={{ width: tableWidth, minWidth: tableWidth }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: NAME_COL_PX + 'px' }} />
              {data.map((_, i) => <col key={i} style={{ width: PER_EMP_PX + 'px' }} />)}
            </colgroup>
            <thead>
              {/* Header row — Employee names rotated, full names (no truncation) */}
              <tr style={{ backgroundColor: '#000' }}>
                <th style={{ color: '#fff', padding: '6px 6px', fontSize: '11px', textAlign: 'left', borderBottom: '2px solid #333', verticalAlign: 'bottom' }}>
                  Status \ Employee
                </th>
                {data.map((d, i) => (
                  <th
                    key={i}
                    style={{
                      color: '#fff',
                      padding: '6px 2px',
                      fontSize: '11px',
                      fontWeight: 700,
                      borderBottom: '2px solid #333',
                      borderLeft: '1px solid rgba(255,255,255,0.08)',
                      verticalAlign: 'bottom',
                      height: '180px',
                    }}
                  >
                    <div style={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      whiteSpace: 'nowrap',
                      margin: '0 auto',
                      letterSpacing: '0.5px',
                    }}>
                      {(d.fullName || d.name || '').toUpperCase()}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* DONE row */}
              <tr style={{ backgroundColor: '#111' }}>
                <td style={{ color: '#00B050', fontWeight: 'bold', fontSize: '12px', padding: '8px 6px', whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#00B050', marginRight: '4px' }}>■</span> DONE
                </td>
                {data.map((d, i) => (
                  <td key={i} style={{ color: '#00B050', textAlign: 'center', fontSize: '13px', fontWeight: '800', padding: '8px 2px', borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
                    {d.done ?? 0}
                  </td>
                ))}
              </tr>
              {/* INPROCESS row */}
              <tr style={{ backgroundColor: '#181818' }}>
                <td style={{ color: '#FFD700', fontWeight: 'bold', fontSize: '12px', padding: '8px 6px', whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#FFD700', marginRight: '4px' }}>■</span> INPROCESS
                </td>
                {data.map((d, i) => (
                  <td key={i} style={{ color: '#FFD700', textAlign: 'center', fontSize: '13px', fontWeight: '800', padding: '8px 2px', borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
                    {d.inprocess ?? 0}
                  </td>
                ))}
              </tr>
              {/* PENDING row */}
              <tr style={{ backgroundColor: '#111' }}>
                <td style={{ color: '#FF5555', fontWeight: 'bold', fontSize: '12px', padding: '8px 6px', whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#FF5555', marginRight: '4px' }}>■</span> PENDING
                </td>
                {data.map((d, i) => (
                  <td key={i} style={{ color: '#FF5555', textAlign: 'center', fontSize: '13px', fontWeight: '800', padding: '8px 2px', borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
                    {d.pending ?? 0}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PerformanceSection({ performance, perfChartData }) {
  const [search, setSearch] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [localPerf, setLocalPerf] = useState(performance || []);
  useEffect(() => { setLocalPerf(performance || []); }, [performance]);

  const COLS = ['presEntry', 'dailySchedule', 'dailyWork', 'leavePlanning', 'reporting',
    'issueHandling', 'ctc', 'financialMgmt', 'teamMgmt', 'dressCode', 'skills', 'knowledge',
    'behaviour', 'habits', 'gossiping', 'credit', 'privacy', 'stuti', 'hardworking', 'leadership', 'nature', 'health'];
  const COL_LABELS = ['Attendance', 'Daily Sch.', 'Daily Work', 'Leave Plan', 'Reporting',
    'Issue Hdlg', 'CTC', 'Fin. Mgmt', 'Team Mgmt', 'Dress Code', 'Skills', 'Knowledge',
    'Behaviour', 'Habits', 'Gossiping', 'Credit', 'Privacy', 'Stuti', 'Hardworking', 'Leadership', 'Nature', 'Health'];

  const filtered = localPerf.filter(r => (r.name || '').toLowerCase().includes(search.toLowerCase()));

  const handleEdit = (name, field, value) => {
    setLocalPerf(prev => prev.map(r => r.name === name ? { ...r, [field]: value } : r));
    updateReportCell('performance', name, field, value);
  };

  const completePct = (r) => {
    const done = COLS.filter(c => (r[c] || '').toUpperCase() === 'DONE').length;
    return Math.round((done / COLS.length) * 100);
  };

  // Chart 1 - metric completion rate (scrollable)
  const metricData = COLS.map((col, i) => ({
    name: COL_LABELS[i],
    done: localPerf.filter(r => (r[col] || '').toUpperCase() === 'DONE').length,
    total: localPerf.length,
  })).map(d => ({ ...d, pct: d.total > 0 ? Math.round(d.done / d.total * 100) : 0 }));
  const chart1Width = Math.max(metricData.length * 70 + 80, 700);

  // Chart 2 - per employee DONE/INPROCESS/PENDING counts (dark bg, scrollable)
  const empPerfData = localPerf.map(r => {
    let done = 0, inp = 0, pending = 0;
    COLS.forEach(col => {
      const v = (r[col] || '').toUpperCase().trim();
      if (v === 'DONE') done++;
      else if (['INP', 'INP.', 'INPROCESS', 'IN WORKING', 'INFROCESS'].includes(v)) inp++;
      else pending++;
    });
    return { name: r.name || '', fullName: r.name || '', done, inprocess: inp, pending };
  });

  return (
    <div className="space-y-5">
      {/* ALL EMPLOYEE PERFORMANCE REPORT — Grouped DONE/INPROCESS/PENDING bars + integrated table */}
      <DarkGroupedBarChart
        data={empPerfData}
        title="ALL EMPLOYEE PERFORMANCE REPORT"
        height={420}
      />

      {/* Chart 1 - Metric completion rate (scrollable) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Performance Metric Completion Rate (All {metricData.length} Metrics)</CardTitle>
        </CardHeader>
        <CardContent className="pr-2">
          <div className="overflow-x-auto pb-2">
            <div style={{ width: chart1Width, minWidth: chart1Width }}>
              <BarChart width={chart1Width} height={220} data={metricData} margin={{ top: 5, right: 20, left: 10, bottom: 55 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-40} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={v => [`${v}%`, 'Completion']} />
                <Bar isAnimationActive={false} dataKey="pct" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-semibold">Performance Report ({filtered.length} employees)</CardTitle>
            <div className="flex items-center gap-2">
              <EditBtn editMode={editMode} onToggle={() => setEditMode(m => !m)} />
              <div className="relative w-48">
                <Search className="absolute left-2 top-2 w-4 h-4 text-stone-400" />
                <Input className="pl-8 h-8 text-xs" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-stone-50 border-b">
                <tr>
                  <th className="px-2 py-2 text-left sticky left-0 bg-stone-50 min-w-[130px]">Name</th>
                  <th className="px-2 py-2 text-center">%</th>
                  {COL_LABELS.map(l => <th key={l} className="px-2 py-2 text-center whitespace-nowrap">{l}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((r, i) => {
                  const pct = completePct(r);
                  return (
                    <tr key={i} className="hover:bg-stone-50">
                      <td className="px-2 py-2 font-medium sticky left-0 bg-white min-w-[130px]">{r.name}</td>
                      <td className="px-2 py-2 text-center">
                        <div className="flex items-center gap-1">
                          <div className="w-12 bg-stone-100 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs">{pct}%</span>
                        </div>
                      </td>
                      {COLS.map(col => (
                        <td key={col} className="px-2 py-2 text-center">
                          {editMode
                            ? <EditableStatus val={r[col]} cycle={PERF_CYCLE} onSave={v => handleEdit(r.name, col, v)} />
                            : <StatusBadge val={r[col]} />}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SkillsSection({ skills }) {
  const [search, setSearch] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [localSkills, setLocalSkills] = useState(skills || []);
  useEffect(() => { setLocalSkills(skills || []); }, [skills]);

  const SKILL_COLS = ['excel', 'effects', 'mail', 'companyWebsite', 'todayWorkFile',
    'monthlyDeptWork', 'englishSpeaking', 'ppt', 'typingSpeed', 'orgBehaviour',
    'aDept', 'bAdmin', 'cAllFormat', 'dOther', 'eDailyWork'];
  const SKILL_LABELS = ['Excel', 'Effects', 'Mail', 'Website', 'Work File',
    'Dept Work', 'English', 'PPT', 'Typing', 'Org Bhv',
    'A.Dept', 'B.Admin', 'C.Format', 'D.Other', 'E.Daily Work'];

  const filtered = localSkills.filter(r => (r.name || '').toLowerCase().includes(search.toLowerCase()));

  const handleEdit = (name, field, value) => {
    setLocalSkills(prev => prev.map(r => r.name === name ? { ...r, [field]: value } : r));
    updateReportCell('skills', name, field, value);
  };

  // Chart 1 - skill completion rate per skill (scrollable)
  const skillCompletion = SKILL_COLS.map((col, i) => ({
    name: SKILL_LABELS[i],
    done: localSkills.filter(r => (r[col] || '').toUpperCase() === 'DONE').length,
    pct: localSkills.length > 0
      ? Math.round(localSkills.filter(r => (r[col] || '').toUpperCase() === 'DONE').length / localSkills.length * 100)
      : 0,
  }));
  const chart1Width = Math.max(skillCompletion.length * 70 + 80, 800);

  // Chart 2 - per employee DONE/INPROCESS/PENDING for skills (dark bg)
  const empSkillData = localSkills.map(r => {
    let done = 0, inp = 0, pending = 0;
    SKILL_COLS.forEach(col => {
      const v = (r[col] || '').toUpperCase().trim();
      if (v === 'DONE') done++;
      else if (['INP', 'INP.', 'INPROCESS'].includes(v)) inp++;
      else pending++;
    });
    return { name: (r.name || '').split(' ')[0], done, inprocess: inp, pending };
  });

  return (
    <div className="space-y-5">
      {/* Chart wrapper — flex-col-reverse so the dark ALL DEPARTMENT SKILL REPORT appears above the light Skill Completion chart */}
      <div className="flex flex-col-reverse gap-5">
      {/* Chart 1 - Skill completion per skill (scrollable) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Skill Completion Rate Per Skill (All {skillCompletion.length} Skills)</CardTitle>
        </CardHeader>
        <CardContent className="pr-2">
          <div className="overflow-x-auto pb-2">
            <div style={{ width: chart1Width, minWidth: chart1Width }}>
              <BarChart width={chart1Width} height={220} data={skillCompletion} margin={{ top: 5, right: 20, left: 10, bottom: 55 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={v => [`${v}%`, 'Done']} />
                <Bar isAnimationActive={false} dataKey="pct" fill="#14b8a6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chart 2 - ALL DEPARTMENT SKILL REPORT (dark style, scrollable) */}
      <DarkGroupedBarChart
        data={empSkillData}
        title="ALL DEPARTMENT SKILL REPORT"
        height={300}
      />
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-semibold">Skill Report ({filtered.length} employees)</CardTitle>
            <div className="flex items-center gap-2">
              <EditBtn editMode={editMode} onToggle={() => setEditMode(m => !m)} />
              <div className="relative w-48">
                <Search className="absolute left-2 top-2 w-4 h-4 text-stone-400" />
                <Input className="pl-8 h-8 text-xs" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-stone-50 border-b">
                <tr>
                  <th className="px-2 py-2 text-left sticky left-0 bg-stone-50 min-w-[130px]">Name</th>
                  <th className="px-2 py-2 text-left">Dept</th>
                  <th className="px-2 py-2 text-center">Done</th>
                  {SKILL_LABELS.map(l => <th key={l} className="px-2 py-2 text-center whitespace-nowrap">{l}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((r, i) => (
                  <tr key={i} className="hover:bg-stone-50">
                    <td className="px-2 py-2 font-medium sticky left-0 bg-white min-w-[130px]">{r.name}</td>
                    <td className="px-2 py-2 text-stone-500 whitespace-nowrap">{r.department || '-'}</td>
                    <td className="px-2 py-2 text-center font-semibold text-green-600">{r.doneCount ?? '-'}</td>
                    {SKILL_COLS.map(col => (
                      <td key={col} className="px-2 py-2 text-center">
                        {editMode
                          ? <EditableStatus val={r[col]} cycle={PERF_CYCLE} onSave={v => handleEdit(r.name, col, v)} />
                          : <StatusBadge val={r[col]} />}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SetupSection({ setup, columns }) {
  const [search, setSearch] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [localSetup, setLocalSetup] = useState(setup || []);
  useEffect(() => { setLocalSetup(setup || []); }, [setup]);

  // ALL doc/setup columns from Excel (excluding summary cols which are derived)
  const SUMMARY_COLS = new Set(['DONE', 'INPROCESS', 'PENDING', 'REMARK']);
  const ALL_DOC_COLS = (columns && columns.length > 0)
    ? columns.filter(c => c && !SUMMARY_COLS.has(c))
    : ['RESUME', 'AADHAR CARD', 'PAN CARD', 'PHOTO', 'OFFER LETTER',
       'COMPANY TRANNING', 'JOINING LETTER', 'APPOINTMENT LETTER', 'ID CARD', 'DRESS CODE', 'CTC'];

  const filtered = localSetup.filter(r => (r.name || '').toLowerCase().includes(search.toLowerCase()));

  const handleEdit = (name, field, value) => {
    setLocalSetup(prev => prev.map(r => r.name === name ? { ...r, [field]: value } : r));
    updateReportCell('setup', name, field, value);
  };

  // Document compliance % — for ALL 38 doc/setup fields (scrollable horizontally)
  const docCompletion = ALL_DOC_COLS.map(doc => ({
    name: doc.replace(' CARD', '').replace(' LETTER', ' Ltr'),
    fullName: doc,
    ok: localSetup.filter(r => ['OK', 'DONE', 'YES', 'Y'].includes((r[doc] || '').toString().toUpperCase().trim())).length,
    pct: localSetup.length > 0
      ? Math.round(localSetup.filter(r => ['OK', 'DONE', 'YES', 'Y'].includes((r[doc] || '').toString().toUpperCase().trim())).length / localSetup.length * 100)
      : 0,
  }));

  // Per-employee DONE / INPROCESS / PENDING counts for DOCUMENT & SET UP INDEX chart
  const empSetupData = localSetup.map(r => {
    let done = 0, inp = 0, pending = 0;
    ALL_DOC_COLS.forEach(col => {
      const v = (r[col] || '').toString().toUpperCase().trim();
      if (['OK', 'DONE', 'YES', 'Y', 'COMPLETE', 'COMPLETED'].includes(v)) done++;
      else if (['INP', 'INP.', 'INPROCESS', 'IN PROCESS', 'IN WORKING', 'INFROCESS'].includes(v)) inp++;
      else pending++;
    });
    return { name: r.name || '', fullName: r.name || '', done, inprocess: inp, pending };
  });

  // Width helper: ~70 px per doc bar so all 38 fit, scrollable
  const compChartWidth = Math.max(ALL_DOC_COLS.length * 70 + 80, 800);

  return (
    <div className="space-y-5">
      {/* Chart wrapper — flex-col-reverse so the dark DOCUMENT & SET UP INDEX appears above the light Document Compliance Rate */}
      <div className="flex flex-col-reverse gap-5">
      {/* Document Compliance Rate — covers ALL 38 doc fields, horizontally scrollable */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Document Compliance Rate ({ALL_DOC_COLS.length} document & setup fields)</CardTitle>
        </CardHeader>
        <CardContent className="pr-2">
          <div className="overflow-x-auto pb-2">
            <div style={{ width: compChartWidth, minWidth: compChartWidth }}>
              <BarChart width={compChartWidth} height={260} data={docCompletion} margin={{ top: 10, right: 20, left: 0, bottom: 90 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-40} textAnchor="end" interval={0} height={90} />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={(v, _n, ctx) => [`${v}% (${ctx.payload?.ok || 0}/${localSetup.length})`, ctx.payload?.fullName || 'OK']} />
                <Bar isAnimationActive={false} dataKey="pct" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DOCUMENT & SET UP INDEX — per-employee DONE / INPROCESS / PENDING grouped bar chart (dark, scrollable) */}
      <DarkGroupedBarChart
        data={empSetupData}
        title="DOCUMENT & SET UP INDEX"
        height={300}
      />
      </div>

      {/* Staff Setup Report — ALL 38 doc/setup columns + sticky header + sticky Name + dual scrollbars */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <CardTitle className="text-sm font-semibold">Staff Setup Report ({filtered.length} employees · {ALL_DOC_COLS.length} columns)</CardTitle>
            <div className="flex items-center gap-2">
              <EditBtn editMode={editMode} onToggle={() => setEditMode(m => !m)} />
              <div className="relative w-48">
                <Search className="absolute left-2 top-2 w-4 h-4 text-stone-400" />
                <Input className="pl-8 h-8 text-xs" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto" style={{ maxHeight: 600 }}>
            <table className="text-xs border-collapse" style={{ minWidth: 130 + 60 + ALL_DOC_COLS.length * 100 }}>
              <thead className="sticky top-0 z-20 bg-stone-50">
                <tr className="border-b">
                  <th className="px-2 py-2 text-left font-semibold text-stone-700 sticky left-0 bg-stone-50 z-30 min-w-[130px] border-r border-stone-200">Name</th>
                  <th className="px-2 py-2 text-center font-semibold text-green-600 sticky bg-stone-50 z-30 min-w-[60px] border-r border-stone-200" style={{ left: 130 }}>Done</th>
                  {ALL_DOC_COLS.map(d => (
                    <th key={d} className="px-2 py-2 text-center font-semibold text-stone-700 whitespace-nowrap min-w-[100px] border-r border-stone-200">
                      {d.replace(' CARD', '').replace(' LETTER', ' Ltr')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((r, i) => (
                  <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-stone-50/40'} hover:bg-blue-50/40 transition-colors`}>
                    <td className={`px-2 py-2 font-bold sticky left-0 z-10 min-w-[130px] border-r border-stone-200 ${i % 2 === 0 ? 'bg-white' : 'bg-stone-50/40'}`}>{r.name}</td>
                    <td className={`px-2 py-2 text-center font-semibold text-green-600 sticky z-10 min-w-[60px] border-r border-stone-200 ${i % 2 === 0 ? 'bg-white' : 'bg-stone-50/40'}`} style={{ left: 130 }}>
                      {r['DONE'] ?? '-'}
                    </td>
                    {ALL_DOC_COLS.map(doc => (
                      <td key={doc} className="px-2 py-1.5 text-center border-r border-stone-100">
                        {editMode
                          ? <EditableStatus val={r[doc]} cycle={SETUP_CYCLE} onSave={v => handleEdit(r.name, doc, v)} />
                          : <StatusBadge val={r[doc]} />}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PerfChartSection({ performanceChart }) {
  const [selected, setSelected] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [localPerfChart, setLocalPerfChart] = useState(performanceChart || []);
  useEffect(() => { setLocalPerfChart(performanceChart || []); }, [performanceChart]);

  const METRICS = ['mentally', 'healthy', 'family', 'skill', 'desire', 'knowledge', 'behaviour', 'responsible', 'leader', 'visionSocial'];
  const METRIC_LABELS = ['Mentally', 'Healthy', 'Family', 'Skill', 'Desire', 'Knowledge', 'Behaviour', 'Responsible', 'Leader', 'Vision'];

  const handleEdit = (name, field, value) => {
    setLocalPerfChart(prev => prev.map(r => r.name === name ? { ...r, [field]: value } : r));
    updateReportCell('perfchart', name, field, value);
  };

  const sortedData = localPerfChart
    .filter(r => r.name)
    .sort((a, b) => (b.total || 0) - (a.total || 0));

  const top12 = sortedData.slice(0, 12);
  const top12Width = Math.max(top12.length * 60 + 80, 700);

  const radarData = selected
    ? METRICS.map((m, i) => ({ metric: METRIC_LABELS[i], value: selected[m] || 0 }))
    : null;

  const tableMinWidth = 140 + 60 + METRICS.length * 80;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Performers — VERTICAL bar chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Top Performers (Score out of 10)</CardTitle>
          </CardHeader>
          <CardContent className="pr-2">
            <div className="overflow-x-auto pb-2">
              <div style={{ width: top12Width, minWidth: top12Width }}>
                <BarChart width={top12Width} height={270} data={top12} margin={{ top: 22, right: 10, left: -10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" interval={0} height={65} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${v}/10`, 'Score']} />
                  <Bar isAnimationActive={false} dataKey="total" radius={[4, 4, 0, 0]} maxBarSize={36}>
                    <LabelList isAnimationActive={false} dataKey="total" position="top" style={{ fontSize: '11px', fontWeight: 'bold', fill: '#333' }} />
                    {top12.map((entry, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Radar chart for selected employee */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">
              {selected ? `Radar: ${selected.name}` : 'Click a row below to see radar chart'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {radarData ? (
              <ResponsiveContainer width="100%" height={250}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                  <Radar name="Score" dataKey="value" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.4} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-stone-400 text-sm">
                Select an employee below to view radar chart
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Employees — Table (full name, score, all metrics) */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-semibold">All Employees - Performance Scores ({sortedData.length})</CardTitle>
            <EditBtn editMode={editMode} onToggle={() => setEditMode(m => !m)} />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" style={{ minWidth: tableMinWidth }}>
              <thead className="bg-stone-50 border-b">
                <tr>
                  <th className="px-2 py-2 text-left text-stone-500 w-8">Sr</th>
                  <th className="px-2 py-2 text-left font-semibold text-stone-700 min-w-[140px]" style={{ position: 'sticky', left: 0, backgroundColor: '#f8f7f6', zIndex: 1 }}>Name</th>
                  <th className="px-2 py-2 text-center font-bold text-green-700 min-w-[55px]">Total</th>
                  {METRIC_LABELS.map(l => <th key={l} className="px-2 py-2 text-center whitespace-nowrap text-stone-600">{l}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {sortedData.map((r, i) => (
                  <tr
                    key={i}
                    onClick={() => setSelected(selected?.name === r.name ? null : r)}
                    className={`cursor-pointer transition-colors ${selected?.name === r.name ? 'bg-violet-50' : 'hover:bg-stone-50'}`}
                  >
                    <td className="px-2 py-1.5 text-stone-400">{i + 1}</td>
                    <td className="px-2 py-1.5 font-medium text-stone-800 min-w-[140px]" style={{ position: 'sticky', left: 0, backgroundColor: selected?.name === r.name ? '#f5f3ff' : '#fff', zIndex: 1 }}>
                      {r.name}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold ${
                        (r.total || 0) >= 9 ? 'bg-green-500' : (r.total || 0) >= 7 ? 'bg-amber-500' : (r.total || 0) > 0 ? 'bg-red-400' : 'bg-stone-300'
                      }`}>
                        {editMode ? <EditableNumber val={r.total} min={0} max={10} onSave={v => handleEdit(r.name, 'total', v)} /> : (r.total ?? '-')}
                      </span>
                    </td>
                    {METRICS.map(m => (
                      <td key={m} className="px-2 py-1.5 text-center text-stone-600">
                        {editMode
                          ? <EditableNumber val={r[m]} min={0} max={10} onSave={v => handleEdit(r.name, m, v)} />
                          : (r[m] !== undefined && r[m] !== null ? r[m] : '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function LeaveSection({ weeklyLeave, holidays }) {
  const dayOrder = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', '-'];
  const grouped = {};
  (weeklyLeave || []).forEach(r => {
    const day = (r.weeklyOff || '-').toUpperCase().trim();
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(r);
  });

  const dayCount = dayOrder
    .filter(d => grouped[d])
    .map(d => ({ name: d === '-' ? 'Rotational' : d, value: grouped[d].length }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Day distribution pie */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Weekly Off Day Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie isAnimationActive={false} data={dayCount} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {dayCount.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Holiday list */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Yearly Holiday List</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-56 overflow-y-auto space-y-1">
              {(holidays || []).length === 0 ? (
                <div className="text-stone-400 text-xs py-4 text-center">No holiday data</div>
              ) : (holidays || []).map((h, i) => (
                <div key={i} className="flex items-center justify-between py-1 px-2 rounded hover:bg-stone-50 text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${h.type?.includes('HALF') ? 'bg-amber-400' : 'bg-red-400'}`} />
                    <span className="font-medium text-stone-700">{h.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-stone-500">
                    <span>{h.date}</span>
                    <span className="text-stone-400">{h.day}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${h.type?.includes('HALF') ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                      {h.type?.includes('HALF') ? 'Half Day' : 'Full Day'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly off table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Weekly Off Planning ({(weeklyLeave || []).length} employees)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {dayOrder.filter(d => grouped[d]).map(day => (
              <div key={day} className="border rounded-lg overflow-hidden">
                <div className={`px-3 py-2 text-xs font-bold text-white ${
                  day === 'SUNDAY' ? 'bg-rose-500' :
                  day === 'MONDAY' ? 'bg-blue-500' :
                  day === 'TUESDAY' ? 'bg-violet-500' :
                  day === 'WEDNESDAY' ? 'bg-teal-500' :
                  day === 'THURSDAY' ? 'bg-amber-500' :
                  day === 'FRIDAY' ? 'bg-green-500' :
                  'bg-stone-500'
                }`}>
                  {day === '-' ? 'Rotational / TBD' : day} ({grouped[day].length})
                </div>
                <div className="divide-y">
                  {grouped[day].map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                      <span className="font-medium text-stone-700">{r.name}</span>
                      <span className="text-stone-500 text-xs">{r.department || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function HRIndexSection({ index1, index2 }) {
  const [search, setSearch] = useState('');
  const [activeIdx, setActiveIdx] = useState('index1');
  const [editMode, setEditMode] = useState(false);
  const [localIdx1, setLocalIdx1] = useState(index1 || []);
  const [localIdx2, setLocalIdx2] = useState(index2 || []);
  useEffect(() => { setLocalIdx1(index1 || []); }, [index1]);
  useEffect(() => { setLocalIdx2(index2 || []); }, [index2]);

  const data = activeIdx === 'index1' ? localIdx1 : localIdx2;
  const setData = activeIdx === 'index1' ? setLocalIdx1 : setLocalIdx2;
  const filtered = data.filter(r => (r.particular || '').toLowerCase().includes(search.toLowerCase()));

  const handleEdit = (srNo, field, value) => {
    setData(prev => prev.map(r => r.srNo === srNo ? { ...r, [field]: value } : r));
    updateReportCell(activeIdx, null, field, value, srNo);
  };

  const statusCounts = {
    done: data.filter(r => (r.status || '').toUpperCase() === 'DONE').length,
    inprocess: data.filter(r => ['INPROCESS', 'INFROCESS', 'IN WORKING'].includes((r.status || '').toUpperCase())).length,
    pending: data.filter(r => (r.status || '').toUpperCase() === 'PENDING').length,
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <button onClick={() => setActiveIdx('index1')} className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${activeIdx === 'index1' ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}>
          HR Index 1 ({(index1 || []).length})
        </button>
        <button onClick={() => setActiveIdx('index2')} className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${activeIdx === 'index2' ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}>
          HR Index 2 ({(index2 || []).length})
        </button>
        <div className="flex items-center gap-3 ml-auto text-xs">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />{statusCounts.done} Done</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />{statusCounts.inprocess} In Process</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />{statusCounts.pending} Pending</span>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-semibold">{activeIdx === 'index1' ? 'HR Department Index 1' : 'HR Department Index 2'}</CardTitle>
            <div className="flex items-center gap-2">
              <EditBtn editMode={editMode} onToggle={() => setEditMode(m => !m)} />
              <div className="relative w-48">
                <Search className="absolute left-2 top-2 w-4 h-4 text-stone-400" />
                <Input className="pl-8 h-8 text-xs" placeholder="Search task..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold w-10">Sr</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold">Particular / Task</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold">Contact Person</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold">Remark</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((r, i) => (
                  <tr key={i} className="hover:bg-stone-50">
                    <td className="px-3 py-2 text-xs text-stone-400">{r.srNo || i + 1}</td>
                    <td className="px-3 py-2 font-medium text-stone-800">{r.particular}</td>
                    <td className="px-3 py-2 text-stone-500 text-xs">
                      {editMode ? <EditableText val={r.contactPerson} onSave={v => handleEdit(r.srNo || i + 1, 'contactPerson', v)} /> : (r.contactPerson || '-')}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {editMode
                        ? <EditableStatus val={r.status} cycle={INDEX_CYCLE} onSave={v => handleEdit(r.srNo || i + 1, 'status', v)} />
                        : <StatusBadge val={r.status} />}
                    </td>
                    <td className="px-3 py-2 text-xs text-stone-500">
                      {editMode ? <EditableText val={r.remark} onSave={v => handleEdit(r.srNo || i + 1, 'remark', v)} /> : (r.remark || '-')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// MAIN HRReportsTab Component
// ============================================================

export default function HRReportsTab() {
  const [section, setSection] = useState('staff-advance');
  const [overview, setOverview] = useState(null);
  const [ctcData, setCtcData] = useState({ ctc: [], totals: {}, joiningDates: {} });
  const [perfData, setPerfData] = useState([]);
  const [skillData, setSkillData] = useState([]);
  const [setupData, setSetupData] = useState({ setup: [], columns: [] });
  const [perfChartData, setPerfChartData] = useState([]);
  const [leaveData, setLeaveData] = useState({ weeklyLeave: [], holidays: [] });
  const [indexData, setIndexData] = useState({ index1: [], index2: [] });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadSection = async (sec) => {
    setSection(sec);
    // Only sections that need API data trigger a load
    const apiSection = sec === 'salary-payroll' ? 'ctc' : sec;
    setLoading(true);
    try {
      if (apiSection === 'ctc' && ctcData.ctc.length === 0) {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/hr/reports/ctc`, { headers }),
          axios.get(`${API}/hr/attendance-employees`, { headers }).catch(() => ({ data: { employees: [] } })),
        ]);
        const empList = r2.data.employees || r2.data || [];
        const joiningDates = {};
        empList.forEach(e => {
          const name = (e.employeeName || '').toUpperCase().trim();
          if (name) joiningDates[name] = e.joiningDate || e.staffIndex?.joiningDate || null;
        });
        setCtcData({ ctc: r1.data.ctc, totals: r1.data.totals, joiningDates });
      } else if (apiSection === 'performance') {
        const tasks = [];
        if (perfData.length === 0) {
          tasks.push(axios.get(`${API}/hr/reports/performance`, { headers }).then(r => setPerfData(r.data.performance)));
        }
        if (perfChartData.length === 0) {
          tasks.push(axios.get(`${API}/hr/reports/performance-chart`, { headers }).then(r => setPerfChartData(r.data.performanceChart)));
        }
        if (tasks.length > 0) await Promise.all(tasks);
      } else if (apiSection === 'skills' && skillData.length === 0) {
        const r = await axios.get(`${API}/hr/reports/skills`, { headers });
        setSkillData(r.data.skills);
      } else if (apiSection === 'perfchart' && perfChartData.length === 0) {
        const r = await axios.get(`${API}/hr/reports/performance-chart`, { headers });
        setPerfChartData(r.data.performanceChart);
      } else if (apiSection === 'leave' && leaveData.weeklyLeave.length === 0) {
        const r = await axios.get(`${API}/hr/reports/weekly-leave`, { headers });
        setLeaveData({ weeklyLeave: r.data.weeklyLeave, holidays: r.data.holidays });
      }
    } catch (err) {
      toast.error('Failed to load report data');
    }
    setLoading(false);
  };

  useEffect(() => { /* lazy load sections on click */ }, []);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await axios.post(`${API}/hr/reports/import`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported: ${r.data.summary.ctcRecords} CTC, ${r.data.summary.performanceRecords} Performance, ${r.data.summary.skillRecords} Skill records`);
      // Reset all cached data to force reload
      setOverview(null);
      setCtcData({ ctc: [], totals: {} });
      setPerfData([]);
      setSkillData([]);
      setSetupData({ setup: [], columns: [] });
      setPerfChartData([]);
      setLeaveData({ weeklyLeave: [], holidays: [] });
      setIndexData({ index1: [], index2: [] });
      loadSection(section);
    } catch (err) {
      toast.error('Import failed: ' + (err.response?.data?.detail || err.message));
    }
    setImporting(false);
    e.target.value = '';
  };

  const handleExport = () => {
    const link = document.createElement('a');
    link.href = `${API}/hr/reports/export`;
    link.download = 'HR_DEPARTMENT_ALL_REPORT.xlsx';
    link.click();
  };

  const SECTIONS = [
    { id: 'staff-advance', label: '1. Staff Advance Work', icon: ClipboardList },
    { id: 'daily-work-report', label: '2. Daily Work Report Idx', icon: ClipboardList },
    { id: 'daily-schedule-report', label: '3. Daily Schedule Report Idx', icon: ClipboardList },
    { id: 'staff-report', label: '4. Staff Report Index', icon: ClipboardList },
    { id: 'attendance', label: '5. Attendance', icon: Calendar },
    { id: 'daily-work', label: '6. Daily Work', icon: FileText },
    { id: 'daily-schedule', label: '7. Daily Schedule', icon: Clock },
    { id: 'ctc', label: '8. CTC Report', icon: IndianRupee },
    { id: 'performance', label: '9. Performance', icon: TrendingUp },
    { id: 'skills', label: '10. Skill', icon: Star },
    { id: 'perfchart', label: '11. Performance Chart', icon: Award },
    { id: 'leave', label: '12. Leave Planning', icon: Calendar },
    { id: 'company-hierarchy', label: '13. Company Hierarchy', icon: Users },
    { id: 'project-hierarchy', label: '14. Project Hierarchy', icon: BarChart2 },
    { id: 'salary-payroll', label: '15. Salary / Payroll', icon: IndianRupee },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-stone-800">HR Department All Report</h2>
          <p className="text-xs text-stone-500">Based on HR DEPARTMENT ALL REPORT 31.03.2026</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="reports-import-btn"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            {importing ? 'Importing...' : 'Import Excel'}
          </Button>
          <Button
            size="sm"
            onClick={handleExport}
            className="bg-stone-800 hover:bg-stone-700 text-white"
            data-testid="reports-export-btn"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2" data-testid="reports-section-nav">
        {SECTIONS.map(s => (
          <SectionButton
            key={s.id}
            active={section === s.id}
            onClick={() => loadSection(s.id)}
            icon={s.icon}
            label={s.label}
          />
        ))}
      </div>

      {/* Content */}
      <div data-testid="reports-content">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-stone-400">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading report data...
          </div>
        ) : (
          <>
            {/* New 7 sub-tabs */}
            {section === 'staff-advance' && <StaffAdvanceWorkIndexTab />}
            {section === 'daily-work-report' && <DailyWorkReportIndexTab />}
            {section === 'daily-schedule-report' && <DailyScheduleReportIndexTab />}
            {section === 'staff-report' && <StaffReportIndexTab />}
            {section === 'attendance' && <MonthlyAttendanceTab />}
            {section === 'daily-work' && <DailyWorkTab />}
            {section === 'daily-schedule' && <DailyScheduleTab />}

            {/* Existing sub-tabs (kept) */}
            {section === 'ctc' && <CTCSection ctc={ctcData.ctc} totals={ctcData.totals} joiningDates={ctcData.joiningDates} />}
            {section === 'performance' && <PerformanceSection performance={perfData} perfChartData={perfChartData} />}
            {section === 'skills' && <SkillsSection skills={skillData} />}
            {section === 'perfchart' && <PerfChartSection performanceChart={perfChartData} />}
            {section === 'leave' && <LeaveSection weeklyLeave={leaveData.weeklyLeave} holidays={leaveData.holidays} />}

            {/* New hierarchies */}
            {section === 'company-hierarchy' && <CompanyHierarchyTab />}
            {section === 'project-hierarchy' && <ProjectHierarchyTab />}

            {/* Salary / Payroll — 4-sub-tab layout (Index / Presenty / Calculation / Salary Slip) */}
            {section === 'salary-payroll' && <SalaryPayrollTab />}
          </>
        )}
      </div>
    </div>
  );
}
